import React from 'react';
import { ContentBlock } from '@jbhi-fi/components/src/ContentBlock';
import { BreadcrumbProps, Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import { InfinitePagination } from '../../components/InfinitePagination/InfinitePagination';
import { useCards } from '@jbhi-fi/components/src/hooks/useCards';
import { useSubCategoryProps } from '../../hooks/content/useSubCategoryProps';
import { ContentfulFilter } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { NoResultsPlaceholder } from '@jbhi-fi/components/src/NoResultsPlaceholder/NoResultsPlaceholder';
import { HeroBlock, HeroBlockProps } from '@jbhi-fi/components/src/HeroBlock';
import { CardGridCardProps, CardGrid } from '@jbhi-fi/components/src/Card/CardGrid/CardGrid';
import { HeadingDivider } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { getNoCardsPlaceholderProps } from '@jbhi-fi/components/src/Contentful/transformers/CardGridTransformer';
import { styles } from './SubCategory.css';
import { LegacyContentTheme } from '@jbhi-fi/components/src/Contentful/transformers/ThemeTransformer';
import { EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { getDateOnly } from '@jbhi-fi/utils/src/date';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

const FEATURED_BANNER_CARD_INDEX = 2;

export type SubCategoryProps = {
  breadcrumbs: BreadcrumbProps;
  theme?: LegacyContentTheme;
  hero?: HeroBlockProps;
  featuredBannerProps?: CardGridCardProps;
  cardsFilter?: Omit<ContentfulFilter, 'content_type'>;
  pageTitle?: string;
  pinnedCards?: Array<CardGridCardProps>;
  pinnedCardsHeading?: string;
};

export const SubCategory = () => {
  const isDesktop = useBreakpoint('md', 'up');
  const { data, isLoading, error } = useSubCategoryProps() || {};
  const {
    breadcrumbs = { items: [] },
    hero,
    theme,
    featuredBannerProps,
    pageTitle,
    pinnedCards,
    pinnedCardsHeading,
    cardsFilter,
  } = data || {};
  const cardFilters = React.useMemo(
    () =>
      cardsFilter
        ? [
            {
              ...cardsFilter,
              content_type: EntryType.article,
              order: '-fields.publishedDate',
            },
            {
              ...cardsFilter,
              content_type: EntryType.competition,
              'fields.endDate[gte]': getDateOnly(new Date()),
              'fields.hideFromCardGrid[ne]': 'true',
            },
            {
              ...cardsFilter,
              content_type: EntryType.genericPage,
              order: '-fields.publishedDate',
              'fields.heroLogo[exists]': 'true',
            },
          ]
        : [],
    [cardsFilter]
  );
  const { cards: cardsProp = [], totalCards, loadMoreCards } = useCards({
    filters: cardFilters,
    numCards: (isDesktop ? 12 : 6) - (featuredBannerProps ? 1 : 0),
    useLoadingSkeletons: true,
  });
  const cards = [...cardsProp];
  if (featuredBannerProps) {
    cards.splice(FEATURED_BANNER_CARD_INDEX, 0, featuredBannerProps);
  }
  const visibleCards = cards.length - (featuredBannerProps ? 1 : 0);
  const backgroundColor = theme?.backgroundColor;

  if (isLoading || error || !cardsFilter) return null;
  return (
    <>
      <Breadcrumbs className={styles.breadcrumbs} backgroundColor={backgroundColor} {...breadcrumbs} />
      <ContentBlock
        style={{ backgroundColor: theme?.backgroundColor }}
        paddingBottom={{ mobile: 3.5, tablet: 10 }}
        paddingTop={0}
      >
        {hero ? <HeroBlock {...hero} /> : null}
      </ContentBlock>
      {pinnedCards?.length ? (
        <ContentBlock style={{ backgroundColor: palette.secondary.main }} maxWidth="full" paddingX={0}>
          <ContentBlock
            paddingY={10}
            className={styles.pinnedCardsGrid}
            maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
          >
            {pinnedCardsHeading ? <HeadingDivider heading={pinnedCardsHeading} /> : null}
            <CardGrid cards={pinnedCards} isCarousel={false} />
          </ContentBlock>
        </ContentBlock>
      ) : null}
      <ContentBlock
        paddingTop={{ mobile: 10, tablet: 15 }}
        paddingBottom={10}
        maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
      >
        {cards.length !== 0 ? (
          <CardGrid cards={cards} isCarousel={false} />
        ) : (
          <NoResultsPlaceholder {...getNoCardsPlaceholderProps(pageTitle)} />
        )}
      </ContentBlock>
      {cards.length !== 0 ? (
        <ContentBlock paddingTop={0} paddingBottom={{ mobile: 15, tablet: 20 }}>
          <div className={styles.paginationContainer}>
            <InfinitePagination
              showProgressBar
              showProgressText
              currentItemsCount={visibleCards}
              totalItemsCount={totalCards}
            />
            {visibleCards < totalCards ? (
              <JBButton type="primary" onClick={loadMoreCards} classes={styles.loadMoreBtn}>
                Load more
              </JBButton>
            ) : null}
          </div>
        </ContentBlock>
      ) : null}
    </>
  );
};
